import type {ListAdvanceRequestsStatus} from '@api/model';

/** Advance status, as the list endpoint's `status` query param accepts it. */
export type AdvanceStatusFilter = NonNullable<ListAdvanceRequestsStatus>;

interface StatusOption {
  value: AdvanceStatusFilter;
  label: string;
}

/**
 * Identity function that only accepts an option list covering EVERY status.
 *
 * Two guarantees, both at compile time, both needed:
 *
 * - a status the API has and the list omits demands a `missingStatuses`
 *   property the argument cannot have, so the call fails to compile;
 * - a status the API does not have fails the constraint on `T`.
 *
 * The odd-looking intersection is there for the error message. Collapsing the
 * parameter type to `never` compiles and fails too, but reports only "not
 * assignable to parameter of type 'never'", which tells the reader nothing.
 * This form names the statuses to add.
 *
 * Lifted from the reimbursement twin rather than shared: the two lists are
 * different statuses with different labels, and the only thing genuinely
 * common is this six-line trick.
 */
const withEveryStatus = <const T extends ReadonlyArray<StatusOption>>(
  options: T &
    (Exclude<AdvanceStatusFilter, T[number]['value']> extends never
      ? unknown
      : {missingStatuses: Exclude<AdvanceStatusFilter, T[number]['value']>}),
): T => options;

/**
 * The status dropdown on the All tab, and the only list of raw advance
 * statuses in the app.
 *
 * Lifecycle order, not alphabetical: the list is read as a pipeline, so
 * "Draft → Submitted → Approved → Paid" scans and an alphabetical run does not.
 * It is the same order `list-tabs.ts` ranks its tabs by, and for the same
 * reason.
 *
 * **These are the API's statuses, not the pills' labels.** `status.ts` holds a
 * view-layer projection that splits `paid` into Disbursed and Pending Receipt
 * by whether a receipt is still owed — a distinction only the server can make,
 * and one this filter cannot ask for. So "Paid" here matches rows whose pill
 * reads either, and the two are separate TABS (Disbursed / Awaiting receipt)
 * for anyone who needs them apart.
 *
 * The dropdown renders this, the route validates against it, and the route
 * test iterates it. Three hand-written copies is how a status the API adds
 * starts validating in the URL while staying unselectable in the UI, with
 * nothing failing.
 */
export const STATUS_OPTIONS = withEveryStatus([
  {value: 'draft', label: 'Draft'},
  {value: 'submitted', label: 'Submitted'},
  {value: 'approved', label: 'Approved'},
  {value: 'partially_approved', label: 'Partly approved'},
  {value: 'paid', label: 'Paid'},
  {value: 'receipt_submitted', label: 'Awaiting verification'},
  {value: 'closed', label: 'Closed'},
  {value: 'rejected', label: 'Rejected'},
  {value: 'withdrawn', label: 'Withdrawn'},
  {value: 'failure_of_proof', label: 'Failure of proof'},
]);

/** The valid `?status=` values, derived so they cannot drift from the dropdown. */
export const STATUS_VALUES: ReadonlyArray<AdvanceStatusFilter> = STATUS_OPTIONS.map((o) => o.value);

/**
 * `?status=` → a real status, or `undefined` for anything else.
 *
 * A search param is user-editable text in a shareable link, so junk is expected
 * traffic; forwarding it would 422 the whole list rather than one row.
 */
export function parseStatus(value: unknown): AdvanceStatusFilter | undefined {
  return STATUS_VALUES.find((s) => s === value);
}
